import type { Activity, MeetingView, RecipientView, RejectReason } from '@sorted/types'
import { useState } from 'react'
import { CalendarDays } from 'lucide-react'
import { ACTIVITY_ICON } from '../components/icons.js'
import { Screen } from '../components/Screen.js'
import { VenueSlider } from '../components/VenueSlider.js'
import { api } from '../lib/api.js'
import { activityLabel, meetingWhenLong } from '../lib/format.js'
import { t } from '../lib/i18n.js'

/**
 * The one engaged screen - spec §7.
 *
 * Shows the top three options and asks for one. A deliberate departure from
 * invariant 5 ("one suggestion at a time"), taken with the tradeoff
 * understood: the spec's bet is that comparison is the disease, so the number
 * to watch is seconds-to-confirmed (§15). Three is the cap, so it stays a
 * short list rather than a feed.
 *
 * Two ways off this screen: pick one, or leave. There is no 'show me others' -
 * the chips that used to do that read as a request while actually spending a
 * rejection, so they moved to the exit and now only record why.
 */

const CHIP_KEY: Record<RejectReason, `chip.${RejectReason}`> = {
  too_far: 'chip.too_far',
  too_pricey: 'chip.too_pricey',
  too_loud: 'chip.too_loud',
  not_my_thing: 'chip.not_my_thing',
}

interface Props {
  token: string
  meeting: RecipientView
  onUpdate: (view: MeetingView) => void
}

export function Suggestion({ token, meeting, onUpdate }: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Showing the "why didn't these work" question, on the way out. */
  const [leaving, setLeaving] = useState(false)

  const Icon = ACTIVITY_ICON[meeting.activity as Activity]

  // `venues` is the list, `venue` the top one - fall back so an older payload
  // still renders.
  const venues =
    meeting.venues.length > 0 ? meeting.venues : meeting.venue ? [meeting.venue] : []

  async function act(fn: () => Promise<MeetingView>) {
    setBusy(true)
    setError(null)
    try {
      onUpdate(await fn())
    } catch {
      setError(t('suggest.failed'))
    } finally {
      setBusy(false)
    }
  }

  if (venues.length === 0) {
    // Should not happen - invariant 9 guarantees something to show. If it
    // does, offer the human handoff rather than a dead end.
    return (
      <Screen centered>
        <h1>{t('suggest.nothing')}</h1>
        <p className="muted">{t('suggest.nothingHint')}</p>
      </Screen>
    )
  }

  return (
    <Screen>
      <>
          {/* On the pinned-area path this is the recipient's FIRST screen - he
              was asked nothing, so the explanation the origin screen used to
              carry has to live here or he never gets it at all. */}
          {/* The whole invitation as ONE sentence: what they were invited to
              and where, in the words a person would use. The activity sat
              under this as a separate line, which made "Brunch" read as a
              category label rather than as what someone is asking them to do.
              
              Split into parts rather than interpolated, so the two facts can
              carry emphasis without putting markup in a translation string —
              and the languages order them differently anyway. */}
          <p className="invite-intro center">
            {meeting.areaName ? (
              <>
                {t('origin.invitedInBefore')}{' '}
                <strong>{activityLabel(meeting.activity as Activity)}</strong>{' '}
                {t('origin.invitedInMiddle')} <strong>{meeting.areaName}</strong>{' '}
                {t('origin.invitedInAfter')}
              </>
            ) : (
              <>
                {t('origin.invitedForBefore')}{' '}
                <strong>{activityLabel(meeting.activity as Activity)}</strong>{' '}
                {t('origin.invitedForAfter')}
              </>
            )}
          </p>

          <div className="summary summary-center mt-sm">
            <span className="fact">
              <CalendarDays size={16} aria-hidden />
              {meetingWhenLong(meeting.meetAt)}
            </span>
          </div>
          <h2 className="mt-sm center">
            {venues.length === 1
              ? t('suggest.pickOneSingle')
              : t('suggest.pickOne', { n: String(venues.length) })}
          </h2>
          {/* Why the set looks the way it does. With a pinned area the
              invitation line above already names it, so repeating it here
              would say the same thing twice in three lines. */}
          {!meeting.areaName && <p className="faint mt-sm">{t('suggest.between')}</p>}
      </>

      <div className="mt">
        <VenueSlider
          venues={venues}
          token={token}
          busy={busy}
          cta={t('suggest.thisOne')}
          onPick={(placeId) => void act(() => api.accept(token, placeId))}
        />
      </div>

      {error && (
        <p className="faint error mt" role="status">
          {error}
        </p>
      )}

      {/* In flow after the cards, not pinned to the viewport bottom. These are
          what you do INSTEAD of picking one, so they belong where the options
          run out. */}
      <div className="stack mt">
        {/* The way out. Every path must end in a decision or a person
            (invariant 9) - this is the person. */}
        {leaving ? (
          <div className="reasons">
            {/* Asked only here, and only once. Sitting under the cards these
                read as "show me others", which is not what they did - each one
                spent a rejection. As the last question before someone leaves
                they are what they always were: why these three missed. */}
            <span className="faint">{t('chip.leavePrompt')}</span>
            <div className="chips">
              {meeting.rejectChips.map((r) => (
                <button
                  key={r}
                  className="chip"
                  disabled={busy}
                  onClick={() => void act(() => api.optOutRecipient(token, r))}
                >
                  {t(CHIP_KEY[r])}
                </button>
              ))}
            </div>
            {/* Answering must stay optional - someone who will not explain
                themselves still gets to leave. */}
            <button
              className="btn btn-ghost mt-sm"
              disabled={busy}
              onClick={() => void act(() => api.optOutRecipient(token))}
            >
              {t('chip.noReason')}
            </button>
          </div>
        ) : (
          <button className="btn" disabled={busy} onClick={() => setLeaving(true)}>
            {t('suggest.selfServe')}
          </button>
        )}
      </div>
    </Screen>
  )
}
